// src/App.jsx
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  setUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectClientAccess,
  selectIsAdmin,
} from "./store/authSlice";
import {
  observeAuthState,
  getUserProfile,
  mockUser,
} from "./services/firebase";

import Auth from "./components/Auth/Auth";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import AdminRoute from "./components/Auth/AdminRoute";
import ClientApp from "./components/ClientApp/ClientApp";
import AdminDashboard from "./components/Admin/AdminDashboard";
import ClientList from "./components/Admin/ClientList";
import ClientEditor from "./components/Admin/ClientEditor";
import GeographyLibrary from "./components/Admin/GeographyLibrary";
import FaqEditor from "./components/Admin/FaqEditor";
import NotFound from "./components/UI/NotFound";
import LoadingSpinner from "./components/UI/LoadingSpinner";

const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

// Demo clients accessible without authentication
const DEMO_SLUGS = ["sample-laundry-co", "sample-catering-co"];

/**
 * Redirects an authenticated user to their first accessible client,
 * or to /admin if they are an admin with no client access.
 */
const DefaultClientRedirect = () => {
  const clientAccess = useSelector(selectClientAccess);
  const isAdmin = useSelector(selectIsAdmin);

  if (clientAccess.length > 0) {
    return <Navigate to={`/${clientAccess[0]}`} replace />;
  }

  // Admin with no client access goes to admin dashboard
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // No client access and not admin: stay on login
  return <Navigate to="/login" replace />;
};

/**
 * Main App component with routing
 */
function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loading = useSelector(selectAuthLoading);

  useEffect(() => {
    // Dev mode: auto-login with mock user
    if (DEV_MODE) {
      console.log("🔧 DEV MODE: Auto-login with mock user");
      dispatch(setUser(mockUser));
      return;
    }

    // Production: observe Firebase auth state
    const unsubscribe = observeAuthState(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userProfile = await getUserProfile(firebaseUser.uid);
          if (userProfile) {
            dispatch(
              setUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName:
                  userProfile.displayName || firebaseUser.displayName || "",
                role: userProfile.role || "client",
                clientAccess: userProfile.clientAccess || [],
              }),
            );
          } else {
            dispatch(setUser(null));
          }
        } catch (error) {
          console.error("Error loading user profile:", error);
          dispatch(setUser(null));
        }
      } else {
        dispatch(setUser(null));
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  // Show loading while checking auth, unless the visitor is heading
  // to a demo client (those don't require auth to resolve)
  if (loading && !DEV_MODE) {
    const slug = window.location.pathname.split("/")[1];
    const isDemoClient = DEMO_SLUGS.includes(slug);

    if (!isDemoClient) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="large" message="Loading..." />
        </div>
      );
    }
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <DefaultClientRedirect /> : <Auth />}
        />

        {/* Admin routes — nested under AdminDashboard layout */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        >
          <Route index element={<ClientList />} />
          <Route path="clients/new" element={<ClientEditor />} />
          <Route path="clients/:slug" element={<ClientEditor />} />
          <Route path="geography" element={<GeographyLibrary />} />
          <Route path="faq" element={<FaqEditor />} />
        </Route>

        {/* Client routes */}
        <Route
          path="/:clientSlug/*"
          element={
            <ProtectedRoute>
              <ClientApp />
            </ProtectedRoute>
          }
        />

        {/* Redirects */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <DefaultClientRedirect />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
